import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';

/**
 * Complenia product add-on services (e.g. reinforced packaging), added as nested cart lines.
 * Ported from Gioielloro (gl-product-addons.js), reduced to a single checkbox per service.
 *
 * Each <cmp-product-addon> lives outside the product form and writes
 * `shopify-additional-items[i][...]` hidden inputs associated with the form through the `form`
 * attribute; assets/cmp-nested-cart.js turns them into nested `items` for /cart/add.js (product
 * form and "Acquista" button). Inputs follow the form quantity and the selected variant.
 */

const ADDITIONAL_ITEMS = 'shopify-additional-items';

/** @type {Set<string>} */
const registeredForms = new Set();

/** @param {string} formId */
function getForm(formId) {
  const form = document.getElementById(formId);
  return form instanceof HTMLFormElement ? form : null;
}

/**
 * Rewrites the additional-item inputs of every add-on bound to the form.
 * Indices start at 1, as required by accelerated checkout.
 * @param {string} formId
 */
function syncForm(formId) {
  const form = getForm(formId);
  if (!form) return;

  const parentId = /** @type {HTMLInputElement | null} */ (form.elements.namedItem('id'))?.value ?? '';
  const quantityInput = /** @type {HTMLInputElement | null} */ (form.elements.namedItem('quantity'));
  const quantity = String(Math.max(1, Number(quantityInput?.value) || 1));

  let index = 1;
  const addons = document.querySelectorAll(`cmp-product-addon[data-form-id="${CSS.escape(formId)}"]`);

  for (const addon of addons) {
    if (!(addon instanceof CmpProductAddon)) continue;

    const container = addon.refs.additionalItems;
    if (!container) continue;

    container.replaceChildren();
    if (!addon.isSelected || !addon.dataset.variantId) continue;

    const prefix = `${ADDITIONAL_ITEMS}[${index}]`;
    for (const [name, value] of [
      [`${prefix}[id]`, addon.dataset.variantId],
      [`${prefix}[quantity]`, quantity],
      [`${prefix}[parent_id]`, parentId],
    ]) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.setAttribute('form', formId);
      container.append(input);
    }
    index++;
  }
}

/**
 * Listens once per form for quantity and variant changes in the surrounding section.
 * @param {string} formId
 * @param {Element} host
 */
function registerForm(formId, host) {
  if (registeredForms.has(formId)) return;

  const section = host.closest('.shopify-section, dialog');
  if (!section) return;

  registeredForms.add(formId);

  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      if (!getForm(formId)) {
        registeredForms.delete(formId);
        return;
      }
      syncForm(formId);
    });
  };

  section.addEventListener('change', schedule);
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, schedule);
  section.addEventListener(StandardEvents.productSelect, (event) => {
    const promise = /** @type {Event & { promise?: Promise<unknown> }} */ (event).promise;
    if (promise) promise.then(schedule, schedule);
    else schedule();
  });
}

/**
 * @typedef {object} AddonRefs
 * @property {HTMLInputElement} [choice] - Checkbox choosing the service.
 * @property {HTMLElement} [additionalItems] - Container for the generated hidden inputs.
 */

/** @extends {Component<AddonRefs>} */
class CmpProductAddon extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { formId } = this.dataset;
    if (!formId) return;

    registerForm(formId, this);
    requestAnimationFrame(() => syncForm(formId));
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.dataset.formId) syncForm(this.dataset.formId);
  }

  get isSelected() {
    return Boolean(this.refs.choice?.checked);
  }

  handleChange() {
    if (this.dataset.formId) syncForm(this.dataset.formId);
  }
}

if (!customElements.get('cmp-product-addon')) {
  customElements.define('cmp-product-addon', CmpProductAddon);
}
